import { SpeechOIcon, LogoutIcon } from '@floriday/floriday-icons'
import {
  CenteredRowStack,
  UserMenuToggleButton,
  UserMenuSubHeader,
  UserMenuRadioItem,
  UserMenu,
  ComboBox,
} from '@floriday/floriday-ui'
import {
  Avatar,
  Box,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { queryClient } from '../../services/queryClient'
import { useRouter } from '@tanstack/react-router'
import { useOktaAuth } from '@okta/okta-react'
import { useUser } from '../../hooks/useUser'
import {
  businessAssociationColors,
  getBusinessAssociationRole,
  getInitials,
} from '../../utils/businessAssociationUtils'
import type { BusinessAssociation } from '../../context/collectingContext'
import { LanguageSelectorDialog } from './LanguageSelectorDialog'
import { useBusinessAssociations } from '../../hooks/useBusinessAssociations'

export const LogisticMeansPortalUserMenu = () => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const { oktaAuth } = useOktaAuth()
  const { user } = useUser()
  const {
    businessAssociations,
    compactBusinessAssociations,
    isLoading,
    selectedBusinessAssociation,
    setSelectedBusinessAssociation,
  } = useBusinessAssociations()

  const name = user?.name || ''

  const handleLogout = async () => {
    setSelectedBusinessAssociation(null)
    queryClient.clear()
    router.invalidate()

    await oktaAuth.signOut()
  }

  const openLanguageModal = () => {
    setAnchorEl(null)
    setIsLanguageModalOpen(true)
  }

  const selectBusinessAssociation = (
    businessAssociation: BusinessAssociation
  ) => {
    setSelectedBusinessAssociation(businessAssociation)
    setAnchorEl(null)
  }

  const findBusinessAssociations = (query: string) => {
    const normalizedQuery = query.trim().toLowerCase()

    return businessAssociations.filter(
      businessAssociation =>
        !normalizedQuery ||
        businessAssociation.id.toString().includes(normalizedQuery) ||
        businessAssociation.name.toLowerCase().includes(normalizedQuery)
    )
  }

  return (
    <CenteredRowStack spacing={2}>
      <UserMenuToggleButton
        data-testid='user-menu-toggle'
        onClick={e => setAnchorEl(e.currentTarget)}
        organization={selectedBusinessAssociation?.name || ''}
        userName={name}
        userPhotoUrl={
          selectedBusinessAssociation
            ? getInitials(selectedBusinessAssociation.name)
            : ''
        }
      />
      <UserMenu anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <UserMenuSubHeader label={t('userMenu.account')} />

        <MenuItem onClick={openLanguageModal}>
          <ListItemIcon>
            <SpeechOIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary={t('userMenu.language.label')} />
        </MenuItem>

        <MenuItem divider onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary={t('userMenu.logout')} />
        </MenuItem>

        <UserMenuSubHeader
          label={`${t('userMenu.businessAssociations')} (${businessAssociations.length})`}
        />

        {!isLoading && businessAssociations.length > 10 && (
          <Box sx={{ px: 1, py: 2 }}>
            <ComboBox<BusinessAssociation>
              fullWidth
              placeholder={t(
                'userMenu.businessAssociationSelector.searchPlaceholder'
              )}
              value={null}
              fetchSuggestions={findBusinessAssociations}
              getDisplayText={businessAssociation => businessAssociation.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={businessAssociation => {
                if (businessAssociation) {
                  selectBusinessAssociation(businessAssociation)
                }
              }}
              renderOption={(props, businessAssociation) => (
                <Box component='li' {...props} sx={{ display: 'flex', gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor:
                        businessAssociationColors[
                          businessAssociation.id %
                            businessAssociationColors.length
                        ],
                    }}
                    variant='rounded'
                  >
                    {getInitials(businessAssociation.name)}
                  </Avatar>
                  <ListItemText
                    primary={businessAssociation.name}
                    secondary={`${businessAssociation.id} - ${getBusinessAssociationRole(businessAssociation.role, i18n.resolvedLanguage)}`}
                  />
                </Box>
              )}
            />
          </Box>
        )}
        {isLoading ? (
          <MenuItem disabled>
            {t('userMenu.loadingBusinessAssociations')}
          </MenuItem>
        ) : (
          compactBusinessAssociations.map((businessAssociation, index) => (
            <Box
              key={businessAssociation.id}
              sx={{
                pb: index === compactBusinessAssociations.length - 1 ? 2 : 0,
              }}
            >
              <UserMenuRadioItem
                primary={businessAssociation.name}
                secondary={`${businessAssociation.id} - ${getBusinessAssociationRole(businessAssociation.role, i18n.resolvedLanguage)}`}
                checked={
                  selectedBusinessAssociation?.id === businessAssociation.id
                }
                onClick={() => selectBusinessAssociation(businessAssociation)}
              >
                <Avatar
                  sx={{
                    bgcolor:
                      businessAssociationColors[
                        index % businessAssociationColors.length
                      ],
                  }}
                  slotProps={{
                    img: { style: { objectFit: 'contain' } },
                  }}
                  variant='rounded'
                >
                  {getInitials(businessAssociation.name)}
                </Avatar>
              </UserMenuRadioItem>
            </Box>
          ))
        )}
      </UserMenu>
      <LanguageSelectorDialog
        open={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />
    </CenteredRowStack>
  )
}
